/**
 * Searching and labelling film stocks. Pure, so the picker's behaviour is
 * tested here rather than through the DOM.
 */
import type { FilmFormat, FilmProcess, FilmStock } from '../api/filmStocks';

export const PROCESS_LABELS: Record<FilmProcess, string> = {
  c41: 'C-41',
  bw: 'B&W',
  e6: 'E-6',
  other: 'Other',
};

export const FORMATS: FilmFormat[] = ['35mm', '120'];

/** "Kodak Portra 400": what the picker shows once a stock is chosen. */
export function stockLabel(stock: Pick<FilmStock, 'brand' | 'name'>): string {
  return stock.brand ? `${stock.brand} ${stock.name}` : stock.name;
}

/**
 * The picker's choice for expired, bulk-rolled or unlabelled film. It isn't a
 * catalogue item (loupe-core `UNKNOWN_FILM_STOCK_ID`); rolls record it as
 * "Unknown film".
 */
export const UNKNOWN_FILM_STOCK: FilmStock = {
  stock_id: 'unknown',
  brand: '',
  name: 'Unknown / unbranded',
  process: 'other',
  formats: [],
  aliases: ['expired', 'bulk', 'unlabelled'],
  dx_codes: [],
  shop_products: [],
  sources: [],
  active: true,
  created_at: '',
  updated_at: '',
};

/** "ISO 400 · C-41 · 35mm, 120": the facts under a stock's name. */
export function stockDetail(stock: Pick<FilmStock, 'box_iso' | 'process' | 'formats'>): string {
  return [
    stock.box_iso ? `ISO ${stock.box_iso}` : null,
    PROCESS_LABELS[stock.process] ?? stock.process,
    stock.formats.length ? stock.formats.join(', ') : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Lower case, accents and punctuation gone: "T-Max" and "tmax" compare equal. */
export function compact(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/** Words of a query, each compacted: "Kodak  T-Max" is ["kodak", "tmax"]. */
function queryWords(query: string): string[] {
  return query.split(/\s+/).map(compact).filter(Boolean);
}

/**
 * How well a stock matches, lower is better, or null when it doesn't: every
 * word of the query must appear in its brand, name, other names or ISO.
 */
function matchRank(stock: FilmStock, words: string[], whole: string): number | null {
  const names = [stock.name, ...stock.aliases].map(compact);
  const brand = compact(stock.brand);
  const haystack = [brand, ...names, `${brand}${names[0]}`, stock.box_iso ? String(stock.box_iso) : ''].join(' ');
  if (!words.every((w) => haystack.includes(w))) return null;
  if (names.some((n) => n.startsWith(whole))) return 0; // "portra" → Portra 400
  if (`${brand}${names[0]}`.startsWith(whole)) return 1; // "kodak portra"
  if (brand.startsWith(words[0] ?? '')) return 2; // "kodak 400"
  return 3;
}

export interface FilmStockGroup {
  brand: string;
  stocks: FilmStock[];
}

export interface FilmStockMatches {
  groups: FilmStockGroup[];
  /** Every match, including those past `limit`. */
  total: number;
}

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/**
 * Active stocks matching `query`, grouped by brand. The brand with the best
 * match comes first ("portra" puts Kodak first); within a brand, better
 * matches first, then by name, so "Pan 50" precedes "Pan 100". At most `limit`
 * stocks are returned.
 */
export function filterFilmStocks(stocks: FilmStock[], query: string, limit = 50): FilmStockMatches {
  const words = queryWords(query);
  const whole = words.join('');
  const ranked = stocks
    .filter((s) => s.active)
    .map((stock) => ({ stock, rank: words.length ? matchRank(stock, words, whole) : 0 }))
    .filter((m): m is { stock: FilmStock; rank: number } => m.rank !== null);

  const byBrand = new Map<string, { brand: string; best: number; members: typeof ranked }>();
  for (const m of ranked) {
    const key = compact(m.stock.brand);
    const group = byBrand.get(key) ?? { brand: m.stock.brand, best: m.rank, members: [] };
    group.best = Math.min(group.best, m.rank);
    group.members.push(m);
    byBrand.set(key, group);
  }

  const groups: FilmStockGroup[] = [];
  let left = limit;
  const ordered = [...byBrand.values()].sort((a, b) => a.best - b.best || collator.compare(a.brand, b.brand));
  for (const group of ordered) {
    if (left <= 0) break;
    const sorted = group.members
      .sort((a, b) => a.rank - b.rank || collator.compare(a.stock.name, b.stock.name))
      .slice(0, left)
      .map((m) => m.stock);
    left -= sorted.length;
    groups.push({ brand: group.brand, stocks: sorted });
  }
  return { groups, total: ranked.length };
}

/**
 * The brand and name a typed query suggests for a new stock: "kodak vision3
 * 250d" is Kodak's "vision3 250d" when Kodak is known, otherwise all name.
 */
export function splitNewStockQuery(query: string, brands: string[]): { brand: string; name: string } {
  const text = query.trim().replace(/\s+/g, ' ');
  const known = [...brands].sort((a, b) => b.length - a.length);
  for (const brand of known) {
    const words = brand.split(' ').length;
    const head = text.split(' ').slice(0, words).join(' ');
    if (compact(head) === compact(brand) && text.length > head.length) {
      return { brand, name: text.slice(head.length).trim() };
    }
  }
  return { brand: '', name: text };
}

/** Every brand in the catalogue, once, in order. */
export function brandsOf(stocks: FilmStock[]): string[] {
  const seen = new Map<string, string>();
  for (const s of stocks) if (!seen.has(compact(s.brand))) seen.set(compact(s.brand), s.brand);
  return [...seen.values()].sort(collator.compare);
}

/** Stocks by brand then name, the way the admin table lists them. */
export function sortStocks(stocks: FilmStock[]): FilmStock[] {
  return [...stocks].sort((a, b) => collator.compare(a.brand, b.brand) || collator.compare(a.name, b.name));
}
